import _ from 'underscore';
import flowModel from './FlowModel';
import flowBuilder from '../flow/build/FlowBuilder';
import FlowUtil from './FlowUtil';
import ViewModelUtil from 'gw-portals-viewmodel-js/ViewModelUtil';

export default {
    /**
     * A factory function that returns a service used to create wizard flows
     *
     * @param {Object} $q - ES6 Promise API implementation
     * @param {Object} EventHub - Event hub instance used to track flow events
     * @param {Array} stepChangeListenerFactories - Array of functions returning a callback that is called for step and exit transitions
     * @param {Object} modalBehavior - Provides promises resulting from displaying information dialogs
     *                                      actionStatusInfo - (modals for displaying progress & error messages of calls to the server)
     *                                      snapshotResetConfirmation - (confirmation message to prompt that data may be lost when reset to snapshot)
     *                                      cancelConfirmation - (confirmation message to cancel flow)
     * @param {Object} [StateUtils]
     *
     * @returns {Function} - Function to call for storing model and flowModel after creation (like in the state)
     **/
    get: function get($q, EventHub, stepChangeListenerFactories, modalBehavior, StateUtils) {

        var dummyModalBehavior = {
            actionStatusInfo: function actionStatusInfo(model) {
                return $q.resolve(model);
            },
            snapshotResetConfirmation: function snapshotResetConfirmation(model) {
                return $q.resolve(model);
            },
            cancelConfirmation: function cancelConfirmation(model) {
                return $q.resolve(model);
            }
        };

        modalBehavior = modalBehavior || dummyModalBehavior;

        /**
         * Create a new flow object
         *
         * @param {Function} flowDefn - function for defining flow definition
         *
         * @returns {Object} A new flow object which can be started or combined with other flows
         **/
        return function (flowDefn) {
            /**
             * Validate Flow
             *  - there is one flow entry and one or more flow exits
             *  - there is only one event associated with a step
             *
             *  @param {Object} flow - Object with entry, exit and steps property
             *  @throws {Error} error with details of how flow is invalid
             *  @returns {true}
             */
            function validateFlow(flow) {
                var validationErrors = [];
                if (!flow.entry) {
                    validationErrors.push('Flow has no entry step defined');
                }
                if (_.keys(flow.exits).length === 0) {
                    validationErrors.push('Flow has no exit steps defined');
                }
                if (validationErrors.length > 0) {
                    throw new Error(validationErrors.join('\n'));
                }
                return true;
            }

            var isStarted = false; // Flag to prevent generating events during wizard bootstrap
            function publishWizardEvent(eventName, model, prevStep, finalStep) {
                if (isStarted) {
                    var unpackedModel = model ? ViewModelUtil.unpackIfViewModelNode(model) : null;
                    EventHub.publish('wizard', eventName, {
                        model: unpackedModel,
                        fromState: prevStep,
                        toState: finalStep,
                        url: StateUtils ? StateUtils.getTrackingUrl() : undefined
                    });
                }
            }

            function withEventsDisabled(fn) {
                isStarted = false;
                try {
                    fn();
                } finally {
                    isStarted = true;
                }
            }

            var flow = flowBuilder(flowDefn, $q, EventHub);
            validateFlow(flow);
            var stepListeners = stepChangeListenerFactories ? stepChangeListenerFactories.map(function (fn) {
                return fn(flow);
            }) : [];

            return {
                /**
                 * A factory function that returns a flow model initialized to the first step
                 *
                 * @param {Object} model         - Model holding initial data object
                 * @param {Object} [extraArgs]   - Map of arguments passed to branch functions and step listeners
                 *
                 * @returns {Object} FlowModel
                 **/
                start: function start(model, extraArgs) {
                    var flowObj = flowModel(flow, $q, publishWizardEvent, stepListeners, modalBehavior);
                    flowObj.snapshot = model;

                    withEventsDisabled(function () {
                        if (FlowUtil.isStepObject(flow.entry)) {
                            // go to the first step triggering any callbacks.
                            flowObj.jumpToStep(flow.entry, model, extraArgs, true);
                        } else {
                            // if junction proceed until a step is reached
                            flowObj.next(model, extraArgs);
                        }
                    });
                    publishWizardEvent('start', model, null, flowObj.currentStep);
                    return flowObj;
                },


                /**
                 * A factory function that returns a flow model which compares the model with the resume expression for each step
                 *
                 * @param {Object} model - Object that is checked against resume expressions for subsequent steps
                 * @param {Object} [extraArgs]   - Map of arguments passed to branch functions and step listeners
                 *
                 *  @returns {Object} FlowModel
                 **/
                resume: function resume(model, extraArgs) {
                    var flowObj = flowModel(flow, $q, publishWizardEvent, stepListeners, modalBehavior);
                    var currentStep = flow.entry;
                    if (FlowUtil.isJunctionObject(currentStep)) {
                        currentStep = FlowUtil.getNextStep(flow, currentStep, model);
                    }
                    var stepHistory = [currentStep];
                    var nextStep = currentStep;
                    do {
                        var stepSkipFn = currentStep.skipOnResumeIf ? currentStep.skipOnResumeIf() : undefined;
                        if (!stepSkipFn || stepSkipFn(model, extraArgs)) {
                            nextStep = FlowUtil.getNextStepOrJunction(flow, nextStep, model);
                            if (nextStep) {
                                currentStep = nextStep;
                                if (FlowUtil.isStepObject(nextStep)) {
                                    stepHistory.push(nextStep);
                                }
                            }
                        } else {
                            nextStep = undefined;
                        }
                    } while (nextStep);

                    if (FlowUtil.isExitPoint(currentStep, flow)) {
                        flowObj.jumpToExit(currentStep, model, extraArgs);
                        return flowObj;
                    }
                    withEventsDisabled(function () {
                        flowObj.resetFlowProgress(currentStep, stepHistory, model);
                        flowObj.jumpToStep(flowObj.currentStep, model, extraArgs, true);
                    });
                    publishWizardEvent('resume', model, null, flowObj.currentStep);
                    return flowObj;
                }
            };
        };
    }
};


//////////////////
// WEBPACK FOOTER
// ./app/customer/js/src/edge/platform/flow/FlowFactory.js
// module id = 99
// module chunks = 0